import { useForm } from 'react-hook-form';

import { Alert, Button, Spinner, TextField } from '@shared';
import { useTranslations } from '@/i18n/client';
import type { OrganizationApi } from '@/services/api/organization-api';
import { EOrganizationType } from '@/services/api/types';
import type { OrganizationSettings, OrganizationUpdateRequest } from '@/services/api/types';

import { useOrganizationSettings } from './useOrganizationSettings';

interface OrganizationSettingsFormData {
  displayName: string;
}

export interface OrganizationSettingsSectionProps {
  /** Test-only injection point for a mock OrganizationApi. */
  organization?: OrganizationApi;
  embedded?: boolean;
}

interface OrganizationSettingsFormProps {
  data: OrganizationSettings;
  embedded: boolean;
  saving: boolean;
  saveError: ReturnType<typeof useOrganizationSettings>['saveError'];
  saved: boolean;
  save: (update: OrganizationUpdateRequest) => Promise<boolean>;
  clearSaveError: () => void;
}

function OrganizationSettingsForm({
  data,
  embedded,
  saving,
  saveError,
  saved,
  save,
  clearSaveError,
}: OrganizationSettingsFormProps) {
  const t = useTranslations('settings');
  const tStatus = useTranslations('common.status');
  const tErrors = useTranslations('errors');
  const sectionClassName = embedded ? 'py-8' : 'md-dashboard__card';

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<OrganizationSettingsFormData>({
    defaultValues: { displayName: data.displayName },
  });

  async function onSubmit(formData: OrganizationSettingsFormData): Promise<void> {
    clearSaveError();
    await save({
      displayName: formData.displayName.trim(),
      organizationType: data.organizationType ?? EOrganizationType.Lab,
    });
  }

  return (
    <section className={sectionClassName}>
      <h2 className={embedded ? 'm-0 mb-1.5 text-[1.35rem]' : undefined}>{t('organization.title')}</h2>
      <p className={embedded ? 'm-0 mb-6 leading-[1.6] text-[var(--zs-muted)]' : undefined}>
        {t('organization.subtitle')}
      </p>

      {saveError ? <Alert variant="error">{tErrors(`api.${saveError}`)}</Alert> : null}
      {saved ? <Alert variant="success">{t('organization.savedMessage')}</Alert> : null}

      <form className="md-form" onSubmit={handleSubmit(onSubmit)} noValidate>
        <fieldset disabled={saving} style={{ border: 'none', padding: 0, margin: 0 }}>
          <TextField
            label={t('organization.displayNameLabel')}
            type="text"
            {...register('displayName', { required: t('organization.errors.displayNameRequired') })}
            message={errors.displayName?.message}
            invalid={Boolean(errors.displayName)}
            required
          />

          <Button className="mt-4" type="submit" loading={saving} loadingLabel={tStatus('working')}>
            {t('organization.saveButton')}
          </Button>
        </fieldset>
      </form>
    </section>
  );
}

export function OrganizationSettingsSection({
  organization,
  embedded = false,
}: OrganizationSettingsSectionProps) {
  const { data, loading, loadError, saving, saveError, saved, save, clearSaveError } =
    useOrganizationSettings(organization);
  const t = useTranslations('settings');
  const tErrors = useTranslations('errors');
  const sectionClassName = embedded ? 'py-8' : 'md-dashboard__card';

  if (loading) {
    return (
      <section className={sectionClassName}>
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <Spinner label={t('organization.title')} />
        </div>
      </section>
    );
  }

  if (loadError) {
    return (
      <section className={sectionClassName}>
        <Alert variant="error">{tErrors(`api.${loadError}`)}</Alert>
      </section>
    );
  }

  if (!data) {
    return null;
  }

  return (
    <OrganizationSettingsForm
      data={data}
      embedded={embedded}
      saving={saving}
      saveError={saveError}
      saved={saved}
      save={save}
      clearSaveError={clearSaveError}
    />
  );
}
